import { CloseIcon, ExclamationCircleIcon } from '@assets/icons';
import { Button, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { AppPrimaryButton } from '@components/ui/AppPrimaryButton/AppPrimaryButton';
import baseStyles from '@components/ui/AppConfirmationModalBase/AppConfirmationModalBase.module.css';
import modalStyles from './AppInactiveProResumeNudgeModal.module.css';

const { Text } = Typography;

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

export type AppInactiveProResumeNudgeModalProps = {
  open: boolean;
  variant: 'inactive_pro' | 'grant_expiring' | 'scheduled_cancel';
  organizationLabel: string;
  grantEndsAt?: string | null;
  planName?: string;
  onDismiss: () => void;
  onResumePro: () => void | Promise<void>;
  isLoading?: boolean;
};

export const AppInactiveProResumeNudgeModal = ({
  open,
  variant,
  organizationLabel,
  grantEndsAt,
  planName,
  onDismiss,
  onResumePro,
  isLoading = false,
}: AppInactiveProResumeNudgeModalProps) => {
  const { t } = useTranslation();
  const translationPrefix =
    variant === 'grant_expiring'
      ? 'organizationBilling.grantExpiringNudge'
      : variant === 'scheduled_cancel'
        ? 'organizationBilling.scheduledCancelNudge'
      : 'organizationBilling.inactiveProResume';
  const formattedGrantEndsAt = formatDateTime(grantEndsAt);
  const paidPlanName = planName || 'Pro';

  return (
    <Modal
      open={open}
      onCancel={onDismiss}
      footer={null}
      destroyOnHidden
      maskClosable={!isLoading}
      closable
      closeIcon={<CloseIcon className={baseStyles.close} />}
      classNames={{
        mask: baseStyles.mask,
        content: baseStyles.content,
        header: baseStyles.header,
        body: baseStyles.body,
      }}
      title={
        <div data-testid="inactive-pro-resume-modal-title">
          <span className={baseStyles.title}>
            {t(`${translationPrefix}.modalTitle`)}
          </span>
          <div className={baseStyles.divider} />
        </div>
      }
    >
      <div className={baseStyles.stack}>
        <div className={modalStyles.alert} role="alert">
          <ExclamationCircleIcon className={modalStyles.alertIcon} aria-hidden />
          <div className={modalStyles.alertBody}>
            <div className={modalStyles.alertTitle}>
              {t(`${translationPrefix}.alertTitle`, {
                plan: paidPlanName,
              })}
            </div>
            <div className={modalStyles.alertText}>
              {t(`${translationPrefix}.alertDescription`, {
                organization: organizationLabel,
                date: formattedGrantEndsAt,
                plan: paidPlanName,
              })}
            </div>
          </div>
        </div>
        <Text className={baseStyles.description}>
          {t(`${translationPrefix}.bodyPrimary`, {
            date: formattedGrantEndsAt,
            plan: paidPlanName,
          })}
        </Text>
        <Text className={baseStyles.description}>
          {t(`${translationPrefix}.bodySecondary`, {
            date: formattedGrantEndsAt,
            plan: paidPlanName,
          })}
        </Text>
        <div className={baseStyles.actions}>
          <Button
            className={baseStyles.cancelButton}
            onClick={onDismiss}
            disabled={isLoading}
          >
            {t(`${translationPrefix}.remindLater`)}
          </Button>
          <AppPrimaryButton
            type="primary"
            className={baseStyles.confirmButton}
            onClick={() => void onResumePro()}
            loading={isLoading}
          >
            {t(`${translationPrefix}.primaryAction`)}
          </AppPrimaryButton>
        </div>
      </div>
    </Modal>
  );
};
